import {
  ENDPOINTS,
  ExecuteVendorImportRequestSchema,
  ListVendorImportsRequestSchema,
  PlanVendorImportRequestSchema,
  type ExecuteVendorImportRequest,
  type ExecuteVendorImportResponse,
  type ListVendorImportsRequest,
  type ListVendorImportsResponse,
  type PlanVendorImportRequest,
  type PlanVendorImportResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';
import { DOCUMENT_PART, METADATA_PART } from './upload';

/**
 * The vendor-list import — CLM-113 + CLM-114 (FR-39, FR-40, FR-40a, DR-12,
 * NFR-19). Three calls behind the Admin **Vendor list** surface, all gated
 * `party:import_vendors` (Admin only) by the API; nothing here is disabled by
 * role (the API is the boundary, NFR-06).
 *
 *  - `listVendorImports` — the batch history and `limits`, read on load so the
 *    page can refuse an oversized file before it is sent (R-113-21).
 *  - `planVendorImport` — the per-row plan. Writes nothing and persists nothing.
 *  - `executeVendorImport` — the same `File` re-sent with the plan's digest
 *    (`planned_sha256`) and the approved rows. The plan is not persisted
 *    (rule 8), so the server re-reads the bytes, recomputes the plan and
 *    refuses with `409` if the file or the registry moved.
 *
 * Both POSTs build their `FormData` exactly as `bundleForm` in `upload.ts` does
 * (CLM-57): `metadata` as an `application/json` Blob, `document` as the user's
 * own `File`. Every metadata object is validated `.strict()` before it leaves
 * the browser; every response is parsed through the registry schema, never
 * asserted.
 *
 * The plan carries vendor names and addresses. Nothing in this module (or in
 * the surface) writes it to `localStorage` / `sessionStorage`.
 */
const LIST = ENDPOINTS['list-vendor-imports'];
const PLAN = ENDPOINTS['plan-vendor-import'];
const EXECUTE = ENDPOINTS['execute-vendor-import'];

function sheetForm(metadata: unknown, sheet: File): FormData {
  const form = new FormData();
  form.append(METADATA_PART, new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append(DOCUMENT_PART, sheet, sheet.name);
  return form;
}

export async function listVendorImports(
  request: ListVendorImportsRequest = {},
  signal?: AbortSignal,
): Promise<ListVendorImportsResponse> {
  const validated = ListVendorImportsRequestSchema.parse(request);
  /* `page` travels as dotted keys — `apiGet` does not expand a nested object. */
  const query: Record<string, string | number> = {};
  if (validated.cloudeq_entity_key !== undefined) query['cloudeq_entity_key'] = validated.cloudeq_entity_key;
  if (validated.page?.limit !== undefined) query['page.limit'] = validated.page.limit;
  if (validated.page?.cursor !== undefined) query['page.cursor'] = validated.page.cursor;
  const raw = await apiGet<unknown>(LIST.path, {
    query: query satisfies Query,
    ...(signal === undefined ? {} : { signal }),
  });
  return LIST.response.parse(raw);
}

export async function planVendorImport(
  request: PlanVendorImportRequest,
  sheet: File,
  signal?: AbortSignal,
): Promise<PlanVendorImportResponse> {
  const metadata = PlanVendorImportRequestSchema.parse(request);
  const raw = await apiPost<unknown>(PLAN.path, sheetForm(metadata, sheet), signal === undefined ? {} : { signal });
  return PLAN.response.parse(raw);
}

export async function executeVendorImport(
  request: ExecuteVendorImportRequest,
  sheet: File,
  signal?: AbortSignal,
): Promise<ExecuteVendorImportResponse> {
  const metadata = ExecuteVendorImportRequestSchema.parse(request);
  const raw = await apiPost<unknown>(EXECUTE.path, sheetForm(metadata, sheet), signal === undefined ? {} : { signal });
  return EXECUTE.response.parse(raw);
}

/**
 * What a `multipart/form-data` body adds around its two parts: the boundary
 * lines and each part's `Content-Disposition` / `Content-Type` headers. A
 * browser's boundary is ~40 bytes and the headers ~150 a part, so 1 KiB is a
 * generous allowance; the file name is counted separately (it is the user's).
 */
export const MULTIPART_OVERHEAD_BYTES = 1024;

/**
 * The size the execute body will have, measured before it is sent (R-113-21):
 * the file, the metadata JSON as UTF-8, the file name (it travels in the
 * part header) and {@link MULTIPART_OVERHEAD_BYTES}. The server buffers every
 * body against `CLM_API_MAX_REQUEST_BYTES` and answers a bare `413` the Admin
 * cannot act on, so the page compares this with `limits.max_request_bytes`
 * and says so instead of sending.
 */
export function executeBodyBytes(request: ExecuteVendorImportRequest, sheet: File): number {
  const metadataBytes = new TextEncoder().encode(JSON.stringify(request)).length;
  const nameBytes = new TextEncoder().encode(sheet.name).length;
  return sheet.size + metadataBytes + nameBytes + MULTIPART_OVERHEAD_BYTES;
}
